import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useCurrency } from '../../utils/currency';
import { useI18n } from '../../i18n/I18nContext';
import './CalculateurHypotheque.css';

const PRESETS = [
  { id: 'bonifie1', label: 'Taux bonifie (revenu ≤ 6x SNMG)', taux: 1, source: 'CPA - Credit immobilier bonifie' },
  { id: 'bonifie3', label: 'Taux bonifie (revenu 6-12x SNMG)', taux: 3, source: 'CPA - Credit immobilier bonifie' },
  { id: 'moyen', label: 'Taux moyen marche', taux: 6.86, source: 'Banque d\'Algerie, S2 2025' },
  { id: 'custom', label: 'Taux personnalise', taux: null, source: null }
];

function CalculateurHypotheque() {
  const [prix, setPrix] = useState('');
  const [apport, setApport] = useState('');
  const [duree, setDuree] = useState(20);
  const [presetId, setPresetId] = useState('moyen');
  const [tauxCustom, setTauxCustom] = useState(6.86);
  const [resultat, setResultat] = useState(null);
  const [showAmortissement, setShowAmortissement] = useState(false);

  const getTaux = () => {
    const preset = PRESETS.find(p => p.id === presetId);
    return preset && preset.taux !== null ? preset.taux : tauxCustom;
  };

  const { formatPrix } = useCurrency();
  const { t } = useI18n();

  const calculer = () => {
    const prixNum = Number(prix);
    const apportNum = Number(apport) || 0;

    if (!prixNum || prixNum <= 0) return;
    if (apportNum >= prixNum) return;

    const montantEmprunt = prixNum - apportNum;
    const taux = getTaux();
    const tauxMensuel = taux / 100 / 12;
    const nbMois = duree * 12;

    let mensualite;
    if (taux === 0) {
      mensualite = montantEmprunt / nbMois;
    } else {
      const puissance = Math.pow(1 + tauxMensuel, nbMois);
      mensualite = montantEmprunt * (tauxMensuel * puissance) / (puissance - 1);
    }

    const coutTotal = mensualite * nbMois;
    const coutInterets = coutTotal - montantEmprunt;

    // Tableau amortissement
    const tableau = [];
    let capitalRestant = montantEmprunt;
    for (let mois = 1; mois <= nbMois; mois++) {
      const interetsMois = capitalRestant * tauxMensuel;
      const principalMois = mensualite - interetsMois;
      capitalRestant -= principalMois;
      tableau.push({
        mois,
        mensualite: Math.round(mensualite),
        principal: Math.round(principalMois),
        interets: Math.round(interetsMois),
        capitalRestant: Math.max(0, Math.round(capitalRestant))
      });
    }

    setResultat({ mensualite, montantEmprunt, coutTotal, coutInterets, taux, tableau });
    setShowAmortissement(false);
  };

  return (
    <div className="calculateur-page">
      <Link to="/outils" className="btn btn-secondary">&larr; Outils</Link>

      <h1>{t.tools.calcTitle}</h1>
      <p className="calc-subtitle">{t.tools.calcDesc}</p>

      <div className="calc-layout">
        {/* Formulaire */}
        <div className="calc-form glass-card">
          <div className="form-group">
            <label>{t.tools.price}</label>
            <input
              type="number"
              placeholder="Ex: 25000000"
              value={prix}
              onChange={e => setPrix(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label>{t.tools.downPayment}</label>
            <input
              type="number"
              placeholder="Minimum 10% recommande"
              value={apport}
              onChange={e => setApport(e.target.value)}
            />
            {prix && apport && Number(prix) > 0 && (
              <span className="form-hint">
                {Math.round((Number(apport) / Number(prix)) * 100)}% du prix
              </span>
            )}
          </div>

          <div className="form-group">
            <label>{t.tools.duration} : {duree}</label>
            <input
              type="range"
              min="5"
              max="35"
              value={duree}
              onChange={e => setDuree(Number(e.target.value))}
              className="range-input"
            />
            <div className="range-labels">
              <span>5 ans</span>
              <span>35 ans</span>
            </div>
          </div>

          <div className="form-group">
            <label>{t.tools.rate}</label>
            <div className="presets-grid">
              {PRESETS.map(preset => (
                <button
                  key={preset.id}
                  className={`preset-btn ${presetId === preset.id ? 'active' : ''}`}
                  onClick={() => {
                    setPresetId(preset.id);
                    if (preset.taux !== null) setTauxCustom(preset.taux);
                  }}
                >
                  <span className="preset-label">{preset.label}</span>
                  {preset.taux !== null && <span className="preset-taux">{preset.taux}%</span>}
                  {preset.source && <span className="preset-source">{preset.source}</span>}
                </button>
              ))}
            </div>
          </div>

          {presetId === 'custom' && (
            <div className="form-group">
              <label>Taux personnalise : {tauxCustom}%</label>
              <input
                type="range"
                min="0.5"
                max="8"
                step="0.05"
                value={tauxCustom}
                onChange={e => setTauxCustom(Number(e.target.value))}
                className="range-input"
              />
              <div className="range-labels">
                <span>0.5%</span>
                <span>8%</span>
              </div>
            </div>
          )}

          <button className="btn btn-primary calc-btn" onClick={calculer}>
            {t.tools.calcBtn}
          </button>

          <p className="disclaimer">
            Les taux affiches sont indicatifs (sources : Banque d'Algerie, CPA).
            Consultez votre banque pour un taux personnalise.
          </p>
        </div>

        {/* Resultats */}
        {resultat && (
          <div className="calc-results glass-card">
            <h2>{t.tools.calcTitle}</h2>

            <div className="result-main">
              <div className="result-mensualite">
                <span className="result-label">{t.tools.monthly}</span>
                <span className="result-value">{formatPrix(resultat.mensualite)}</span>
              </div>
            </div>

            <div className="result-details">
              <div className="result-item">
                <span className="result-label">{t.tools.price}</span>
                <span className="result-value">{formatPrix(resultat.montantEmprunt)}</span>
              </div>
              <div className="result-item">
                <span className="result-label">{t.tools.totalCost}</span>
                <span className="result-value">{formatPrix(resultat.coutTotal)}</span>
              </div>
              <div className="result-item">
                <span className="result-label">{t.tools.totalInterest}</span>
                <span className="result-value accent">{formatPrix(resultat.coutInterets)}</span>
              </div>
              <div className="result-item">
                <span className="result-label">Taux applique</span>
                <span className="result-value">{resultat.taux}%</span>
              </div>
              <div className="result-item">
                <span className="result-label">Duree</span>
                <span className="result-value">{duree} ans ({duree * 12} mois)</span>
              </div>
            </div>

            {/* Barre visuelle interets vs principal */}
            <div className="result-bar-section">
              <span className="result-label">Repartition du cout total</span>
              <div className="result-bar">
                <div
                  className="result-bar-principal"
                  style={{ width: `${(resultat.montantEmprunt / resultat.coutTotal) * 100}%` }}
                >
                  {t.tools.principal}
                </div>
                <div
                  className="result-bar-interets"
                  style={{ width: `${(resultat.coutInterets / resultat.coutTotal) * 100}%` }}
                >
                  {t.tools.interest}
                </div>
              </div>
            </div>

            <button
              className="btn btn-secondary toggle-amort"
              onClick={() => setShowAmortissement(!showAmortissement)}
            >
              {t.tools.amortTable}
            </button>

            {showAmortissement && (
              <div className="amortissement-container">
                <table className="amortissement-table">
                  <thead>
                    <tr>
                      <th>{t.tools.month}</th>
                      <th>{t.tools.monthly}</th>
                      <th>{t.tools.principal}</th>
                      <th>{t.tools.interest}</th>
                      <th>{t.tools.balance}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {resultat.tableau.map(row => (
                      <tr key={row.mois}>
                        <td>{row.mois}</td>
                        <td>{row.mensualite.toLocaleString('fr-DZ')}</td>
                        <td>{row.principal.toLocaleString('fr-DZ')}</td>
                        <td>{row.interets.toLocaleString('fr-DZ')}</td>
                        <td>{row.capitalRestant.toLocaleString('fr-DZ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CalculateurHypotheque;
